import { StorageRef, ChartingItemType, ChartingSelectType } from '@caresend/types';
import { capitalize, getUTCTimezoneDate } from '@caresend/utils';
import { defineComponent, computed } from 'vue';
import __vue_component__ from '../../cards/BasicCard/BasicCard.vue.js';
import __vue_component__$2 from '../../inputs/DatePicker/DatePicker.vue.js';
import __vue_component__$3 from '../../inputs/FilePicker/FilePicker.vue.js';
import __vue_component__$4 from '../../inputs/SelectComponent.vue.js';
import __vue_component__$5 from '../../inputs/TextBoxPicker/TextBoxPicker.vue.js';
import __vue_component__$6 from '../../inputs/TextInput/TextInput.vue.js';
import __vue_component__$1 from './ChartingChecklistItemCard.vue.js';
import { usePortalStore } from '../portal/store/portal/index.js';
import { setProcedureChartingItems } from '../portal/store/portal/set/procedure.js';

var script = defineComponent({
    name: 'ChartingItemCard',
    components: {
        BasicCard: __vue_component__,
        ChartingChecklistItemCard: __vue_component__$1,
        DatePicker: __vue_component__$2,
        FilePicker: __vue_component__$3,
        SelectComponent: __vue_component__$4,
        TextBoxPicker: __vue_component__$5,
        TextInput: __vue_component__$6,
    },
    props: {
        procedureID: {
            type: String,
            required: true,
        },
        /**
         * An array of indices to support charting items nested in groups.
         * Example: [1, 2] - Root ChartingItem of index 1, nested non-group
         * ChartingItem of index 2.
         */
        indexArray: {
            type: Array,
            required: true,
        },
    },
    setup(props) {
        const { procedures } = usePortalStore();
        const storagePath = StorageRef.CHARTING;
        const item = computed(() => procedures.getters.chartingItemByIndexArray(props.procedureID, props.indexArray));
        const isChecklist = computed(() => { var _a; return ((_a = item.value) === null || _a === void 0 ? void 0 : _a.type) === ChartingItemType.CHECKLIST; });
        const isDate = computed(() => { var _a; return ((_a = item.value) === null || _a === void 0 ? void 0 : _a.type) === ChartingItemType.DATE; });
        const isFiles = computed(() => { var _a; return ((_a = item.value) === null || _a === void 0 ? void 0 : _a.type) === ChartingItemType.FILES; });
        const isGroup = computed(() => { var _a; return ((_a = item.value) === null || _a === void 0 ? void 0 : _a.type) === ChartingItemType.GROUP; });
        const isNotes = computed(() => { var _a; return ((_a = item.value) === null || _a === void 0 ? void 0 : _a.type) === ChartingItemType.NOTES; });
        const isResults = computed(() => { var _a; return ((_a = item.value) === null || _a === void 0 ? void 0 : _a.type) === ChartingItemType.RESULTS; });
        const isSelect = computed(() => { var _a; return ((_a = item.value) === null || _a === void 0 ? void 0 : _a.type) === ChartingItemType.SELECT; });
        const isText = computed(() => { var _a; return ((_a = item.value) === null || _a === void 0 ? void 0 : _a.type) === ChartingItemType.TEXT; });
        const textBoxPickerItems = computed(() => { var _a, _b, _c; return (_c = (_b = (_a = item.value) === null || _a === void 0 ? void 0 : _a.options) === null || _b === void 0 ? void 0 : _b.map((option) => ({ label: option, value: option }))) !== null && _c !== void 0 ? _c : []; });
        const textBoxPickerHorizontal = computed(() => { var _a; return ((_a = item.value) === null || _a === void 0 ? void 0 : _a.selectType) === ChartingSelectType.TEXT_BOX_PICKER_HORIZONTAL; });
        const setChartingItem = (newItem) => {
            const params = {
                procedureID: props.procedureID,
                newPartialChartingItem: newItem,
                chartingItemIndexArray: props.indexArray,
            };
            setProcedureChartingItems(params);
        };
        const setChartingPhotos = async (encryptedFiles) => {
            if (!encryptedFiles || !item.value)
                return;
            const newItem = {
                files: encryptedFiles,
            };
            setChartingItem(newItem);
        };
        const setText = async (text) => {
            if (!item.value)
                return;
            const newItem = {
                text,
            };
            setChartingItem(newItem);
        };
        const setSelection = async (selection) => {
            if (!item.value)
                return;
            const newItem = {
                selection,
            };
            setChartingItem(newItem);
        };
        /** Currently only supports UTC dates */
        const setDate = async (timestamp) => {
            if (!item.value)
                return;
            const newItem = {
                date: getUTCTimezoneDate(timestamp),
            };
            setChartingItem(newItem);
        };
        const resultsPlaceholder = computed(() => { var _a, _b, _c; return (_b = (_a = item.value) === null || _a === void 0 ? void 0 : _a.resultsPlaceholder) !== null && _b !== void 0 ? _b : (_c = item.value) === null || _c === void 0 ? void 0 : _c.title; });
        return {
            capitalize,
            storagePath,
            isChecklist,
            isDate,
            isFiles,
            isGroup,
            isNotes,
            isResults,
            isSelect,
            isText,
            item,
            resultsPlaceholder,
            setChartingPhotos,
            setDate,
            setSelection,
            setText,
            textBoxPickerItems,
            textBoxPickerHorizontal,
        };
    },
});

export { script as default };
